/**
 * pages/high_risk/prework5a/index.js — Ажлын даалгаврын ажилтны 5А дадлын
 * урьдчилсан шалгалт.
 *
 * Зам: /highrisk/prework5a
 *
 * Урсгал:
 *   1. АШУА ажилтанг "Ажлын даалгавар" төрлөөр бүртгэсний дараа тус ажилтан
 *      системд орох үед энэ хуудас гарч ирнэ.
 *   2. 5А дадлын асуумжуудыг (5а.png seed) бөглөнө (Тийм / Үгүй / Хамаарахгүй).
 *   3. Асуумжууд ЭЕРЭГ томьёололтой ("…аюулгүй юу?", "…бэлэн үү?") тул
 *      "Үгүй" = шаардлага хангаагүй.
 *        • №1 «Аюулыг таних» дээр "Үгүй" → АЮУЛ илэрсэнд тооцож, тухайн
 *          асуумжийн доор анхааруулга гарч, "Ажлын байрны үнэлгээ" модал
 *          нээгдэнэ (HazardFormComposer mode="workplace_assessment").
 *        • Бусад асуумжийн "Үгүй" → зөвхөн зөвшөөрлийг түгжинэ
 *          (аюулын бүртгэл үүсгэхгүй).
 *   4. Шалгалт ажилтныг ТҮГЖИХГҮЙ. Бүх асуумжид хариулмагц "Илгээх" товч
 *      идэвхжиж, хариултууд нь АНХАН ШАТНЫ УДИРДАХ АЖИЛТАНД очно.
 *      Тэр нь ажилтныг ажилд гаргах эсэхийг шийднэ
 *      (oea_worker_registrations.work_approval_status).
 *
 * Чек лист — [[checklistStore]] -ийн "5A" seed (5а.png-ээс).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  Progress,
  Result,
  message as antMessage,
  Tag,
} from "antd";
import Swal from "sweetalert2";

import HazardFormComposer from "src/components/hazard";
import { xabHazardApi } from "src/api/xabApi";
import {
  Card,
  RED,
  PAGE_BG,
  hazardCategoryIds,
} from "src/components/hazard/common";
import { loadChecklist, listChecklist } from "src/pages/risk/reference/checklistStore";
import { useUserContext } from "src/contexts/userContext";
import PageHelp from "src/components/help/PageHelp";
import {
  getMyOeaRegistration,
  getCategories,
  getSeverities,
  createHazard,
  uploadHazardPhoto,
  createPreworkSubmission,
} from "src/api/hazard";

// Аюулын бүртгэл (Ажлын байрны үнэлгээ) үүсгэдэг асуумжийн дугаарууд.
// ЗӨВХӨН №1 «Аюулыг таних — Ажлын байр аюулгүй юу?» дээр «Үгүй» гэвэл
// аюул илэрсэнд тооцож бүртгүүлнэ. Бусад асуумжийн «Үгүй» нь дутагдал
// (PPE, холбоо, эмерженси г.м.) тул зөвшөөрлийг л түгжинэ.
const HAZARD_QUESTION_NOS = [1];
const isHazardQuestion = (item) =>
  !!item && HAZARD_QUESTION_NOS.includes(Number(item.no));

const ANSWERS = {
  // Асуумжууд ЭЕРЭГ өгүүлбэрээр тавигдсан (шаардлага хангасан уу?) тул
  //   ТИЙМ = хэвийн (ногоон), ҮГҮЙ = шаардлага хангаагүй → АЮУЛ (улаан)
  YES: { key: "YES", label: "Тийм", color: "#16A34A", bg: "#EAF6EE" },
  NO: { key: "NO", label: "Үгүй", color: "#DC2626", bg: "#FEEFED" },
  NA: { key: "NA", label: "Хамаарахгүй", color: "#64748B", bg: "#F1F3F6" },
};

export default function PreWork5A() {
  const navigate = useNavigate();
  const { user } = useUserContext();

  // 5А checklist — DB-ээс (кэшээс шууд + шинэчилнэ)
  const [items, setItems] = useState(() => listChecklist("5A"));
  useEffect(() => {
    loadChecklist("5A").then(setItems);
  }, []);
  const [answers, setAnswers] = useState({});

  // Ажлын даалгаврын бүртгэл (NARYAD work_type)
  const [registration, setRegistration] = useState(null);
  const [loadingReg, setLoadingReg] = useState(true);

  // Хазардын модал
  const [hazardOpen, setHazardOpen] = useState(false);
  const [hazardForm, setHazardForm] = useState({});
  const [hazardFlagged, setHazardFlagged] = useState(null);
  // { itemId: hazardReportId } — аль асуумж дээр аль аюулыг бүртгэсэн
  const [reported, setReported] = useState({});
  const [saving, setSaving] = useState(false);

  // Lookup
  const [categories, setCategories] = useState([]);
  const [severities, setSeverities] = useState([]);
  const [hazardList, setHazardList] = useState([]); // xab.hazards эталон жагсаалт

  const myTn = String(user?.info?.tn || user?.tn || "");

  useEffect(() => {
    (async () => {
      try {
        const r = await getMyOeaRegistration({ tn: myTn });
        // Зөвхөн "NARYAD" work_type-той бичлэгийг хүлээж авна.
        // Хэрэв OEA бол энэ хуудас биш — /highrisk/prework15 руу шилжих ёстой.
        if (r && r.work_type !== "NARYAD") {
          navigate("/highrisk/prework15", { replace: true });
          return;
        }
        setRegistration(r);
      } finally {
        setLoadingReg(false);
      }
    })();
    (async () => {
      try {
        const [cat, sev, hz] = await Promise.all([
          getCategories(),
          getSeverities(),
          xabHazardApi.list().catch(() => []),
        ]);
        setCategories(cat.items || []);
        setSeverities(sev.items || []);
        setHazardList(Array.isArray(hz) ? hz : hz?.items || []);
      } catch {
        /* алгасах */
      }
    })();
  }, [myTn, navigate]);

  const answered = useMemo(
    () => items.filter((it) => answers[it.id]).length,
    [items, answers],
  );
  // «Үгүй» гэсэн бүх хариултын тоо — зөвшөөрлийг түгжихэд
  const noCount = useMemo(
    () => items.filter((it) => answers[it.id] === "NO").length,
    [items, answers],
  );
  // Үүнээс АЮУЛ гэж тооцогдох нь зөвхөн аюул таних асуумжийн «Үгүй»
  const hazardCount = useMemo(
    () =>
      items.filter(
        (it) => answers[it.id] === "NO" && isHazardQuestion(it),
      ).length,
    [items, answers],
  );
  const total = items.length;
  const pct = total > 0 ? Math.round((answered / total) * 100) : 0;
  const isComplete = answered === total;

  const setAnswer = (id, key) => {
    setAnswers((prev) => ({ ...prev, [id]: key }));
    if (key === "NO") {
      const item = items.find((it) => it.id === id);
      if (isHazardQuestion(item)) openHazardReport(item);
    }
  };

  const openHazardReport = (item) => {
    setHazardFlagged(item);
    setHazardForm({
      photos: [],
      description: item
        ? `5А асуумж #${item.no} — "${item.title}" → ҮГҮЙ (шаардлага хангаагүй)`
        : "",
      severity: 3,
      deptCode: registration?.department_code || "",
      deptName: registration?.department_name || "",
      sectionCode: registration?.section_code || "",
      sectionName: registration?.section_name || "",
      locationText: registration?.destination || "",
      selfInDanger: true,
    });
    setHazardOpen(true);
  };

  const closeHazard = () => {
    setHazardOpen(false);
    setHazardFlagged(null);
  };

  const submitHazard = async () => {
    if (!hazardForm.description?.trim()) {
      Swal.fire({
        title: "Аюулын тайлбарыг бичнэ үү",
        icon: "warning",
      });
      return;
    }
    setSaving(true);
    try {
      const created = await createHazard({
        location_text: hazardForm.locationText,
        gps_lat: hazardForm.gps?.lat,
        gps_lng: hazardForm.gps?.lng,
        gps_accuracy: hazardForm.gps?.accuracy,
        hazard_category_id: hazardForm.categoryId
          ? Number(hazardForm.categoryId)
          : undefined,
        // Композер 1-3 төрөл сонгуулдаг (categoryIds) — бүгдийг илгээнэ,
        // эс бөгөөс 2 дахь төрөл замдаа алдагдана.
        hazard_category_ids: hazardCategoryIds(hazardForm),
        // xab.hazards-аас сонгосон эталон аюулын id → hazard_reports.hazard_id
        hazard_id: hazardForm.hazardId
          ? Number(hazardForm.hazardId)
          : undefined,
        description: hazardForm.description.trim(),
        voice_file_url: hazardForm.voiceFileUrl || undefined,
        severity: hazardForm.severity,
        affected_location:
          hazardForm.affectedLocation || hazardForm.locationText,
        affected_people_count: hazardForm.peopleCount || undefined,
        self_in_danger: !!hazardForm.selfInDanger,
        photos: hazardForm.photos || [],
        report_mode: "workplace_assessment",
        meta: {
          prework5a_item_id: hazardFlagged?.id,
          prework5a_item_no: hazardFlagged?.no,
          oea_registration_id: registration?.id,
        },
        offline_client_id:
          "wpa-" +
          Date.now() +
          "-" +
          Math.random().toString(36).slice(2, 8),
      });
      // Аюулын id-г хадгална — илгээхэд тухайн асуумжийн мөртэй холбоно
      // (prework_checklist_answer.hazard_report_id).
      if (hazardFlagged?.id) {
        setReported((p) => ({ ...p, [hazardFlagged.id]: created?.id || true }));
      }
      closeHazard();
      await Swal.fire({
        icon: "warning",
        title: "Ажлын байрны үнэлгээ илгээгдлээ",
        text: "Аюулын мэдээллийг АШУА болон ХАБ хэлтэст хүргүүлсэн.",
      });
    } catch {
      antMessage.error("Илгээхэд алдаа гарлаа");
    } finally {
      setSaving(false);
    }
  };

  const [submitting, setSubmitting] = useState(false);

  /**
   * Шалгалтыг ИЛГЭЭХ — ажилтныг түгжихгүй.
   * Бүх хариулт (Үгүй-тэй ч гэсэн) анхан шатны удирдах ажилтанд очиж,
   * тэр нь ажилд гаргах эсэхийг шийднэ.
   */
  const completePreWork = async () => {
    if (!isComplete) {
      antMessage.warning("Бүх асуумжид хариулна уу");
      return;
    }
    if (noCount > 0) {
      const ok = await Swal.fire({
        icon: "warning",
        title:
          hazardCount > 0
            ? "Аюул илрүүлсэн байна"
            : "Шаардлага хангаагүй асуумж байна",
        text:
          "Хариултууд тань анхан шатны удирдах ажилтанд илгээгдэж, ажилд "
          + "гаргах эсэхийг тэр шийднэ. Илгээх үү?",
        showCancelButton: true,
        confirmButtonText: "Илгээх",
        cancelButtonText: "Болих",
      });
      if (!ok.isConfirmed) return;
    }

    setSubmitting(true);
    try {
      // Бөглөлт → xab.prework_checklist_submission.
      // Ажлын ДААЛГАВАР (NARYAD) ажилд 5А бөглөнө. Мөр = энэ ажилтны
      // өөрийн шалгалт (checker_role="WORKER").
      const res = await createPreworkSubmission({
        work_type: "NARYAD",
        checklist_type: "5A",
        checker_role: "WORKER",
        naryad_job_id: registration?.naryad_job_id,
        oea_registration_id: registration?.id,
        work_date: registration?.work_date || undefined,
        tn: myTn,
        department_code: registration?.department_code,
        department_name: registration?.department_name,
        section_code: registration?.section_code,
        section_name: registration?.section_name,
        destination: registration?.destination,
        source: "WEB",
        answers: items.map((it) => ({
          checklist_item_id: it.id,
          item_code: String(it.id),
          item_no: it.no,
          item_title: it.title,
          answer: answers[it.id] || null,
          hazard_report_id:
            typeof reported[it.id] === "number" ? reported[it.id] : undefined,
        })),
        offline_client_id:
          "pw5a-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8),
      });

      // ⚠ 15А-тай ИЖИЛ шалгалт — хоёулаа нэг endpoint ашигладаг тул
      //   ижил алдаанд өртөнө. Дутуу хадгалагдвал ил хэлнэ.
      const sent = res?.answers_sent;
      const ok = res?.answers_saved;
      if (typeof ok === "number" && typeof sent === "number" && ok < sent) {
        await Swal.fire({
          icon: "error",
          title: "Хариулт дутуу хадгалагдлаа",
          html:
            `<div style="text-align:left">${ok}/${sent} хариулт хадгалагдсан.`
            + (res?.answers_failed?.length
              ? `<br/>Асуумж <b>${res.answers_failed.join(", ")}</b> дээр алдаа гарлаа.`
              : "")
            + "<br/><br/>Дахин илгээнэ үү.</div>",
        });
        return;
      }
      await Swal.fire({
        icon: "success",
        title: "Шалгалт илгээгдлээ",
        html:
          `<div style="text-align:left">${total}/${total} асуумжид хариулсан.`
          + (noCount > 0
            ? ` <b>${noCount}</b> асуумжид «Үгүй» гэсэн.`
            : " Бүгд хэвийн.")
          + "<br/><br/>Анхан шатны удирдах ажилтан хариултыг хараад "
          + "<b>ажилд гаргах эсэхийг</b> шийднэ.</div>",
      });
      navigate("/highrisk/list");
    } catch (e) {
      antMessage.error(
        e?.response?.data?.message || "Илгээхэд алдаа гарлаа",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingReg) {
    return (
      <div style={{ background: PAGE_BG, padding: 40 }}>
        Ачааллаж байна...
      </div>
    );
  }

  if (!registration) {
    return (
      <div style={{ background: PAGE_BG, minHeight: "100%", padding: 16 }}>
        <Result
          status="warning"
          title="Танай ажлын даалгаврын бүртгэл өнөөдөр алга"
          subTitle="Ажилд гарахын тулд АШУА таныг ажлын даалгаврын ажилд бүртгэх ёстой."
          extra={
            <button
              type="button"
              onClick={() => navigate("/highrisk/registerTn")}
              style={{
                padding: "10px 22px",
                background: RED,
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              АШУА бүртгэлд очих
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ background: PAGE_BG, minHeight: "100%" }}>
      <style>{`
        .pw-wrap { max-width: 720px; margin: 0 auto; padding: 14px; }
        .pw-grid { display: grid; gap: 14px; grid-template-columns: 1fr; }
        .pw-questions { display: grid; gap: 10px; grid-template-columns: 1fr; }
        .pw-sidebar { display: contents; }
        .pw-submit-wrap { width: 100%; }
        @media (min-width: 720px) {
          .pw-wrap { max-width: 100%; padding: 18px 24px; }
          .pw-questions { grid-template-columns: repeat(2, 1fr); }
        }
        @media (min-width: 1100px) {
          .pw-wrap { max-width: 1280px; }
          .pw-grid { grid-template-columns: 340px minmax(0, 1fr); align-items: start; }
          .pw-sidebar { display: flex; flex-direction: column; gap: 14px; position: sticky; top: 70px; }
          .pw-submit-wrap { max-width: 480px; margin-left: auto; }
        }
        @media (min-width: 1500px) {
          .pw-questions { grid-template-columns: repeat(3, 1fr); }
        }
        .pw-header-inner { max-width: 1280px; margin: 0 auto; }
      `}</style>

      <div
        style={{
          background: "#0050B3",
          color: "#fff",
          padding: "14px 16px",
          position: "sticky",
          top: 0,
          zIndex: 10,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <div
          className="pw-header-inner"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{
              background: "none",
              border: "none",
              color: "#fff",
              fontSize: 18,
              cursor: "pointer",
              padding: 4,
            }}
          >
            <i className="fas fa-arrow-left" />
          </button>
          <div style={{ textAlign: "center", flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              5А дадлын урьдчилсан шалгалт
            </div>
            <div style={{ fontSize: 11, opacity: 0.85 }}>
              Ажлын даалгаврын ажил
            </div>
          </div>
          {/* Баруун талын тэнцвэрийн зайг тусламжийн товчоор
              орлуулав — өргөн нь ижил тул гарчиг голдоо хэвээр. */}
          <PageHelp light size={16} />
        </div>
      </div>

      <div className="pw-wrap">
        <div className="pw-grid">
          {/* Sidebar */}
          <div className="pw-sidebar">
            <Card>
              <div
                style={{
                  fontSize: 11,
                  color: "#7A8496",
                  fontWeight: 700,
                  letterSpacing: 0.4,
                }}
              >
                ӨНӨӨДӨР ТАНЫ БҮРТГЭЛ
              </div>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  marginTop: 6,
                  color: "#2A2F3A",
                }}
              >
                🏢 {registration.department_name || "—"}
              </div>
              {registration.section_name &&
                registration.section_name !== registration.department_name && (
                  <div
                    style={{
                      fontSize: 12.5,
                      color: "#7A8496",
                      marginTop: 2,
                    }}
                  >
                    📍 {registration.section_name}
                  </div>
                )}
              {registration.destination && (
                <div style={{ marginTop: 10 }}>
                  <div
                    style={{
                      fontSize: 10.5,
                      color: "#9A988F",
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    Хаашаа
                  </div>
                  <div
                    style={{
                      fontSize: 12.5,
                      color: "#3A4153",
                      marginTop: 2,
                    }}
                  >
                    {registration.destination}
                  </div>
                </div>
              )}
            </Card>

            <Card>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  marginBottom: 8,
                }}
              >
                <span
                  style={{ fontSize: 14, fontWeight: 800, color: "#2A2F3A" }}
                >
                  Явц: {answered}/{total}
                </span>
                <span
                  style={{
                    fontSize: 18,
                    fontWeight: 900,
                    color: noCount > 0 ? "#DC2626" : "#16A34A",
                  }}
                >
                  {pct}%
                </span>
              </div>
              <Progress
                percent={pct}
                strokeColor={noCount > 0 ? "#DC2626" : "#16A34A"}
                showInfo={false}
              />
              <div style={{ marginTop: 10, display: "flex", gap: 6 }}>
                {hazardCount > 0 && (
                  <Tag color="red">⚠ Аюул илрүүлсэн</Tag>
                )}
                {noCount > hazardCount && (
                  <Tag color="orange">
                    {noCount - hazardCount} шаардлага хангаагүй
                  </Tag>
                )}
                {isComplete && noCount === 0 && (
                  <Tag color="green">✓ Бэлэн</Tag>
                )}
              </div>
            </Card>
          </div>

          {/* Асуумжууд */}
          <div>
            <div className="pw-questions" style={{ marginBottom: 12 }}>
              {items.map((it) => {
                const ans = answers[it.id];
                const ansCfg = ans ? ANSWERS[ans] : null;
                return (
                  <Card
                    key={it.id}
                    style={{
                      borderLeft: ansCfg
                        ? `4px solid ${ansCfg.color}`
                        : "4px solid transparent",
                      background: ansCfg ? ansCfg.bg + "20" : "#fff",
                      transition: "all 0.18s ease",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: 12,
                        alignItems: "flex-start",
                      }}
                    >
                      <div
                        style={{
                          minWidth: 36,
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          background: ansCfg ? ansCfg.bg : "#F1F3F6",
                          color: ansCfg ? ansCfg.color : "#7A8496",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 800,
                          fontSize: 14,
                          border:
                            "2px solid " +
                            (ansCfg ? ansCfg.color : "#E6E9EF"),
                        }}
                      >
                        {String(it.no).padStart(2, "0")}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13.5,
                            fontWeight: 600,
                            color: "#2A2F3A",
                            lineHeight: 1.4,
                          }}
                        >
                          {it.title}
                        </div>
                        {it.description && (
                          <div
                            style={{
                              fontSize: 11.5,
                              color: "#7A8496",
                              marginTop: 4,
                              lineHeight: 1.4,
                            }}
                          >
                            {it.description}
                          </div>
                        )}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: 6,
                            marginTop: 10,
                          }}
                        >
                          {Object.values(ANSWERS).map((a) => {
                            const active = ans === a.key;
                            return (
                              <button
                                key={a.key}
                                type="button"
                                onClick={() => setAnswer(it.id, a.key)}
                                style={{
                                  padding: "8px 4px",
                                  borderRadius: 8,
                                  border:
                                    "1.5px solid " +
                                    (active ? a.color : "#E6E9EF"),
                                  background: active ? a.color : "#fff",
                                  color: active ? "#fff" : a.color,
                                  fontSize: 12.5,
                                  fontWeight: 700,
                                  cursor: "pointer",
                                  transition: "all 0.15s",
                                }}
                              >
                                {a.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* №1 «Аюулыг таних» асуумжид «Үгүй» = аюул илэрсэн →
                        тухайн асуумжийн ЯГ ДООР анхааруулга + мэдээлэх товч. */}
                    {ans === "NO" && isHazardQuestion(it) && (
                      <div
                        style={{
                          marginTop: 10,
                          padding: "8px 10px",
                          background: "#FEEFED",
                          border: "1px solid #F2C8C5",
                          borderRadius: 8,
                          fontSize: 11.5,
                          color: "#B5251D",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <i className="fas fa-exclamation-triangle" />
                        Аюул илрэв — "Ажлын байрны үнэлгээ" мэдээлэх шаардлагатай.
                        <button
                          type="button"
                          onClick={() => openHazardReport(it)}
                          style={{
                            marginLeft: "auto",
                            background: RED,
                            color: "#fff",
                            border: "none",
                            borderRadius: 6,
                            padding: "4px 10px",
                            fontSize: 11.5,
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          Мэдээлэх →
                        </button>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>

            <div className="pw-submit-wrap">
              <button
                type="button"
                onClick={completePreWork}
                disabled={!isComplete || submitting}
                style={{
                  width: "100%",
                  padding: "14px 0",
                  borderRadius: 10,
                  border: "none",
                  // «Үгүй» хариулт байвал ТҮГЖИХГҮЙ — зөвхөн өнгөөр сануулна
                  // (удирдлагын шийдвэр хүсэх төлөв: улбар шар).
                  background: !isComplete
                    ? "#CBD5E1"
                    : noCount > 0
                    ? "#EA580C"
                    : "#16A34A",
                  color: "#fff",
                  fontSize: 15,
                  fontWeight: 800,
                  cursor: isComplete && !submitting ? "pointer" : "not-allowed",
                  boxShadow: isComplete
                    ? "0 4px 12px rgba(22, 163, 74, 0.3)"
                    : "none",
                }}
              >
                <i
                  className={
                    "fas " + (submitting ? "fa-spinner fa-spin" : "fa-paper-plane")
                  }
                  style={{ marginRight: 6 }}
                />
                {submitting
                  ? "Илгээж байна…"
                  : noCount > 0
                  ? "Удирдлагад илгээх (зөвшөөрөл хүсэх)"
                  : "Удирдлагад илгээх"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Аюулын модал — workplace_assessment горимоор */}
      <Modal
        open={hazardOpen}
        onCancel={closeHazard}
        footer={null}
        width={720}
        style={{ maxWidth: "95vw" }}
        destroyOnClose
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              color: RED,
              paddingRight: 28,
            }}
          >
            <i
              className="fas fa-clipboard-list"
              style={{ fontSize: 18, flexShrink: 0 }}
            />
            <span
              style={{
                fontWeight: 800,
                flex: "1 1 auto",
                minWidth: 0,
                fontSize: 14,
                lineHeight: 1.3,
              }}
            >
              Аюулын мэдээлэл — ажлын байрны үнэлгээ
            </span>
            {hazardFlagged && (
              <Tag
                color="red"
                style={{
                  maxWidth: "100%",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  fontSize: 11,
                  lineHeight: 1.3,
                  padding: "2px 8px",
                  margin: 0,
                }}
              >
                #{hazardFlagged.no} — {hazardFlagged.title}
              </Tag>
            )}
          </div>
        }
      >
        <HazardFormComposer
          mode="workplace_assessment"
          value={hazardForm}
          onChange={setHazardForm}
          categories={categories}
          hazards={hazardList}
          // «Аюулын нэр / нэрс» сонгогч — бүртгэлд илүүдэл алхам тул нуув
          showHazardPicker={false}
          severities={severities}
          onPickFile={uploadHazardPhoto}
          onSave={submitHazard}
          onCancel={closeHazard}
          saving={saving}
        />
      </Modal>
    </div>
  );
}
